<script setup>
  import { ref, computed, watch, watchEffect, nextTick } from 'vue';
  import { useApiForecastModels } from '@/composables/useApiForecastModels';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import { useScrollAffordance } from '@/composables/useScrollAffordance';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { sortForecastModels } from '@/utils/sortForecastModels';
  import { FREE_PREVIEW_MODELS, FREE_PREVIEW_ALL_MEMBER_LEVELS_MODELS } from '@/data/forecastPreviewModels';
  import ModelSelectorItem from "@/components/ModelSelectorItem.vue";
  import Icon from "@/components/icons/Icon.vue";
  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();

  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const emit = defineEmits(['input:change', 'models:loaded']);

  const props = defineProps({
    id: {
      type: String,
      required: true
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    bounds: {
      type: Object,
      required: false
    },
    lat: {
      type: [Number, String],
      required: false
    },
    lon: {
      type: [Number, String],
      required: false
    },
    spotId: {
      type: [Number, String],
      required: false
    },
    favSpotId: {
      type: [Number, String],
      required: false
    },
    // Models to merge into the fetched list (e.g. the per-spot Pro Forecast
    // model, which the API does not return). Selector-shaped entries.
    additionalModels: {
      type: Array,
      default: () => []
    },
    // Adds include_nws_marine_forecast=true to the models API request.
    includeNWSMarineForecast: {
      type: Boolean,
      default: false
    },
    // Analytics context for the Forecast_Change event. When provided, a
    // user-driven model change fires that event. Shape:
    //   { initiator: String, spotName?: String, spotId?: Number|String,
    //     lat?: Number|String, lon?: Number|String }
    // Spot_Name is built as `${spotName}-${spotId}` when a named spot is known,
    // otherwise `${lat},${lon}` (with `-${spotId}` appended when a spot id is
    // available). Omit the prop to disable tracking for a given selector.
    analytics: {
      type: Object,
      default: null
    }
  });

  const apiForecastModels = useApiForecastModels();

  const selectedModel = defineModel();
  const fetchedModels = ref(null);
  const loading = ref(true);
  // Guards against out-of-order responses (e.g. the includeNWSMarineForecast
  // flip fires a second fetch while the first is still in flight).
  let fetchToken = 0;

  // Merge injected models (e.g. Pro Forecast) reactively, so they still appear
  // if they become available after the initial fetch. Injected models slot into
  // the premium -> pro -> free ordering (mirrors the legacy ModelSelector), so
  // Pro Forecast lands after the premium models and before the free ones.
  const modelData = computed(() => {
    if (!fetchedModels.value) return null;
    if (!props.additionalModels.length) return fetchedModels.value;

    const premium = fetchedModels.value.filter((m) => m.is_premium && !m.is_pro_only);
    const pro = fetchedModels.value.filter((m) => m.is_pro_only);
    const free = fetchedModels.value.filter((m) => !m.is_premium);

    props.additionalModels.forEach((m) => {
      if (m.is_premium && !m.is_pro_only) premium.push(m);
      else if (m.is_pro_only) pro.push(m);
      else free.push(m);
    });

    return [...premium, ...pro, ...free];
  });

  // Edge-scroll affordances (buttons + fade mask) for the horizontal model list.
  const scrollContainer = ref(null);
  const { canScrollStart, canScrollEnd, fadeEdges, scrollToNext, scrollToStart } = useScrollAffordance(
    scrollContainer,
    { axis: 'horizontal' }
  );

  // Scroll buttons are hidden in the app webview and on narrow screens, where
  // touch scrolling is the expected interaction.
  const isWideEnough = useMediaQuery('(min-width: 600px)');
  const showScrollButtons = computed(() => !$WEB_VIEW && isWideEnough.value);

  async function getData() {
    if (!props.spotId && !props.favSpotId && !props.bounds && !props.lat && !props.lon) {
      loading.value = false;
      console.warn('No spot id, bounds, or lat/lon provided to ModelSelector');
      return
    };

    loading.value = true;

    const token = ++fetchToken;
    const settings = props.includeNWSMarineForecast ? { include_nws_marine_forecast: true } : undefined;
    let response = null;

    if (props.spotId || props.favSpotId) {
      response = await apiForecastModels.bySpot({ spotId: props.spotId, favSpotId: props.favSpotId }, settings);
    } else if (props.lat && props.lon) {
      response = await apiForecastModels.byLatLon(props.lat, props.lon, settings);
    } else if (props.bounds) {
      response = await apiForecastModels.byArea(props.bounds, settings);
    }

    // A newer fetch superseded this one while it was in flight; discard it.
    if (token !== fetchToken) return;

    // response is null when no branch matched above, or undefined if an upstream
    // precondition guard bailed — treat either as a failed fetch.
    if (!response || response.error.value) {
      console.error('Error fetching forecast models', response?.error);
      loading.value = false;
      return
    }

    fetchedModels.value = sortForecastModels(response.data.value.model_summary);
    loading.value = false;

    // A fresh fetch replaces the list; reset the horizontal scroll so the new
    // first (top-sorted) model is visible rather than left clipped offscreen at
    // whatever offset the previous list was scrolled to. Wait for the new items
    // to render before moving the scroller.
    await nextTick();
    scrollToStart();
  }

  // The selection as it stands after the user toggles `modelId`. Derived from
  // the change event rather than read from selectedModel: a parent-bound
  // defineModel only syncs on the parent's re-render, and whether that has
  // happened yet mid-change-event depends on the event being trusted (real
  // clicks run a microtask checkpoint between the v-model listener and ours;
  // synthetic clicks don't) — so selectedModel can be either side of the
  // toggle here. Applying the toggle idempotently is correct from both sides.
  function selectionAfterToggle(modelId, checked) {
    if (!props.multiSelect) return modelId;

    const current = Array.isArray(selectedModel.value) ? selectedModel.value : [];
    if (checked) return current.includes(modelId) ? current : [...current, modelId];
    return current.filter((id) => id !== modelId);
  }

  function handleInputChange(event) {
    // A native change event only fires for user interaction, so everything
    // here is user-driven by construction; programmatic default/restore writes
    // never pass through. The toggled model comes straight off the input.
    const modelId = Number(event.target.value);

    emit('input:change', selectionAfterToggle(modelId, event.target.checked));
    trackForecastChange(modelId);
  }

  // Fire Forecast_Change for the model the user just toggled on or off.
  function trackForecastChange(changedId) {
    if (!props.analytics) return;

    const model = modelData.value?.find((m) => m.model_id === changedId);
    if (!model) return;

    const { initiator, spotName, spotId, lat, lon } = props.analytics;
    const spotIdentity = spotName
      ? `${spotName}-${spotId}`
      : spotId
        ? `${lat},${lon}-${spotId}`
        : `${lat},${lon}`;

    googleAnalyticsStore.sendEvent('Forecast_Change', {
      Model_Name: model.model_short_name || model.model_name,
      Model_Id: model.model_id,
      Initiator: initiator,
      Spot_Name: spotIdentity,
      User_Initiated: true
    });
  }

  watchEffect(() => getData())

  // Surface the resolved model list so the parent can apply default selections.
  watch(modelData, (models) => {
    if (models && models.length) emit('models:loaded', models);
  });

</script>

<template>
  <div class="jwx-model-selector">
    <Transition name="jwx-model-selector__scroll-button">
      <button
        v-if="showScrollButtons && canScrollStart"
        type="button"
        class="jwx-model-selector__scroll-button"
        data-direction="start"
        aria-label="Show previous models"
        @click="scrollToNext('start')"
      >
        <Icon name="chevron-left" />
      </button>
    </Transition>

    <ul ref="scrollContainer" class="jwx-model-selector__model-list" :data-fade="fadeEdges">
      <li v-for="model in modelData" :key="model.model_id" class="jwx-model-selector__model-item">
        <ModelSelectorItem
          :model="model"
          :name="props.id"
          :multiSelect="props.multiSelect"
          :freePreviewModels="FREE_PREVIEW_MODELS"
          :freePreviewAllMemberLevelsModels="FREE_PREVIEW_ALL_MEMBER_LEVELS_MODELS"
          v-model="selectedModel"
          @input:change="handleInputChange"
        />
      </li>

      <li class="jwx-model-selector-actions">
        <a
          class="jwx-model-selector-info-button"
          href="https://help.tempest.earth/hc/en-us/articles/208646557"
          target="_blank"
          rel="noopener"
        >
          <Icon name="info" />
          Model Info
        </a>
      </li>

    </ul>

    <Transition name="jwx-model-selector__scroll-button">
      <button
        v-if="showScrollButtons && canScrollEnd"
        type="button"
        class="jwx-model-selector__scroll-button"
        data-direction="end"
        aria-label="Show more models"
        @click="scrollToNext('end')"
      >
        <Icon name="chevron-right" />
      </button>
    </Transition>
  </div>
</template>